import React from "react";
import { StyleSheet, View } from "react-native";
import { Button, Chip, IconButton, Text, useTheme } from "react-native-paper";
import { NavigationProp, useNavigation } from "@react-navigation/native";
import { RootStackParamList } from "../types/navigationParams";
import { useTranslation } from "react-i18next";
import { CollectionPickerOptions } from "../screens/rentals/CollectionPickerScreen";
import { OutlinedInput } from "./fields/OutlinedInput";
import { Where } from "../services/firestoreServices";
import { FormFieldProps } from "./FormFieldProps";
import {Field} from "../config/companies/config";

interface Item {
  id: number;
  [key: string]: any;
  relatedObjectField?: Item[];
}

type Props = Omit<FormFieldProps, "value"> & {
  value: Item[];
  field: Field & {
    relatedObject?: {
      collection: string;
      key: string;
      secondKey: string;
      multiselect?: boolean;
      countTotal?: boolean;
      formula?: string;
      where?: Where;
    };
  };
};

export function removeDuplicates(arr: Item[]): Item[] {
  if (!Array.isArray(arr)) return [];
  const uniqueIds = new Set();
  return arr.filter((item) => {
    if (uniqueIds.has(item.id)) return false;
    uniqueIds.add(item.id);
    return true;
  });
}

export const MechanicPicker = ({ field, value, onChange, formData }: Props) => {
  const navigation = useNavigation<NavigationProp<RootStackParamList>>();
  const { t } = useTranslation();
  const theme = useTheme();
  const uniqueValues = removeDuplicates(value);

  const openPicker = () => {
    navigation.navigate("CollectionPickerScreen", {
      onValueChange: (newItem: Item) =>
        onChange(field?.name, [...value, newItem]),
      formData,
      ...field,
    } as CollectionPickerOptions);
  };

  const openSecondPicker = (index: number) => {
    const changeObjValue = (selectedVal: Item[]) => {
      const updatedItem: Item = {
        ...value[index],
        relatedObjectField: selectedVal,
      };
      const updatedArray = value.map((item, idx) =>
        idx === index ? updatedItem : item,
      );
      onChange(field.name, updatedArray);
    };

    navigation.navigate("CollectionPickerScreen", {
      onValueChange: changeObjValue,
      collection: field?.relatedObject?.collection || "",
      multiselect: field?.relatedObject?.multiselect,
      key: field.relatedObject?.key || "",
      secondKey: field.relatedObject?.secondKey,
      selectedOptions: value[index].relatedObjectField || [],
      formData: value[index],
      where: field.relatedObject?.where,
    });
  };

  const removeItem = (index: number) => {
    const newValue = value.filter((_, idx) => index !== idx);
    onChange(field?.name, newValue);
  };

  const onIncrement = (item: Item) => {
    onChange(field.name, [...value, item]);
  };

  const onDecrement = (item: Item) => {
    const newValue = value.filter((i) => i.id !== item.id);
    onChange(field.name, newValue);
  };

  const onChangeQuantity = (text: string, item: Item) => {
    const quantity = parseInt(text.replace(/[^0-9]/g, ""), 10) || 0;
    const newVal = Array(quantity).fill(item);
    const newValue = [...value.filter((i) => i.id !== item.id), ...newVal];
    if (quantity > 0) onChange(field.name, newValue);
  };

  const renderRelatedObject = (item: Item[], itemIndex: number) => {
    const updateNestedValue = (nestedItem: Item, delta: number) => {
      const updatedItems = item.map((i) =>
        i.id === nestedItem.id
          ? {
              ...i,
              [field.relatedObject?.secondKey || ""]:
                (parseInt(i[field.relatedObject?.secondKey || ""]) || 0) +
                delta,
            }
          : i,
      );
      const updatedItem: Item = {
        ...value[itemIndex],
        relatedObjectField: updatedItems,
      };
      const newValue = value.map((item, idx) =>
        idx === itemIndex ? updatedItem : item,
      );
      onChange(field.name, newValue);
    };

    if (!Array.isArray(item)) return null;

    return (
      <View style={styles.relatedObjectContainer}>
        {removeDuplicates(item).map((obj, index) => (
          <View key={index} style={styles.relatedObjectItem}>
            <View
              style={[
                styles.chip,
                styles.relatedObjectChip,
                // { backgroundColor: theme.colors.secondaryContainer },
              ]}
            >
              <Text style={{ color: theme.colors.onSecondaryContainer }}>
                {obj[field.relatedObject?.key || ""]}
              </Text>
              <Text style={{ color: theme.colors.onSecondaryContainer }}>
                {t(obj[field.relatedObject?.secondKey || ""])}
              </Text>
            </View>
            <View style={styles.counterContainer}>
              <IconButton
                size={20}
                icon="minus"
                onPress={() => updateNestedValue(obj, -500)}
                mode="contained-tonal"
                iconColor={theme.colors.onSecondary}
              />
              <IconButton
                size={20}
                icon="plus"
                onPress={() => updateNestedValue(obj, +500)}
                mode="contained-tonal"
                iconColor={theme.colors.onSecondary}
              />
            </View>
          </View>
        ))}
      </View>
    );
  };

  const renderActionContent = (index: number, item: Item, items: Item[]) => {
    if (field.relatedObject) {
      return (
        <IconButton
          mode="contained-tonal"
          size={20}
          icon="plus"
          onPress={() => openSecondPicker(index)}
          iconColor={theme.colors.onPrimary}
          style={{ backgroundColor: theme.colors.primary }}
        />
      );
    }

    return (
      <View style={styles.actionsContainer}>
        <IconButton
          mode="contained-tonal"
          size={20}
          icon="minus"
          onPress={() => onDecrement(item)}
          iconColor={theme.colors.onSecondary}
        />
        <OutlinedInput
          onChangeText={(text) => onChangeQuantity(text, item)}
          value={items.length.toString()}
          style={{ height: 40, width: 50 }}
          keyboardType="numeric"
        />
        <IconButton
          mode="contained-tonal"
          size={20}
          icon="plus"
          onPress={() => onIncrement(item)}
          iconColor={theme.colors.onSecondary}
        />
      </View>
    );
  };

  return (
    <View style={{ marginRight: -5 }}>
      <View style={styles.headerContainer}>
        <Text
          variant="titleMedium"
          style={{ color: theme.colors.onBackground }}
        >
          {t(field?.label)}
        </Text>
        <Button onPress={openPicker} mode="text">
          {t("Add")}
        </Button>
      </View>
      {uniqueValues.map((item, index) => {
        const items = value.filter((obj) => obj.id === item.id);
        return (
          <View key={item.id}>
            <View style={styles.itemHeader}>
              <Chip
                onClose={() => removeItem(index)}
                style={{
                  flex: 1,
                  backgroundColor: theme.colors.primaryContainer,
                }}
                textStyle={{ color: theme.colors.onPrimaryContainer }}
              >
                {item[field.key || ""]} {t(item[field.secondKey || ""])}
              </Chip>
              {renderActionContent(index, item, items)}
            </View>
            {field.relatedObject &&
              item.relatedObjectField &&
              renderRelatedObject(item.relatedObjectField, index)}
          </View>
        );
      })}
    </View>
  );
};

const styles = StyleSheet.create({
  headerContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  itemContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  itemContent: {
    // flex: 1,
  },
  itemHeader: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
  },
  chip: {
    flex: 1,
  },
  relatedObjectChip: {
    // marginLeft: 48,
    alignItems: "center",
    // borderRadius: 8,
    paddingVertical: 10,
    paddingLeft: 16,
    flexDirection: "row",
    justifyContent: "space-between",
  },
  actionsContainer: {
    alignItems: "center",
    flexDirection: "row",
  },
  actionButton: {
    borderRadius: 8,
    marginRight: 0,
  },
  counterContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  counterButton: {
    borderRadius: 8,
  },
  relatedObjectContainer: {
    gap: 8,
    marginBottom: 8,
  },
  relatedObjectItem: {
    flexDirection: "row",
    height: 40,
    gap: 4,
    alignItems: "center",
    // marginRight: -5,
  },
  singleRelatedObjectChip: {
    marginLeft: 48,
    marginTop: 8,
  },
});
